import {
  CircleLayerSpecification, FillLayerSpecification,
  GeoJSONSource,
  LayerSpecification,
  LineLayerSpecification,
  LngLatBounds,
  LngLatLike,
  Map as MapLibre,
  RasterLayerSpecification,
  RasterSourceSpecification,
  RasterTileSource, RequestParameters, RequestTransformFunction,
  SourceSpecification,
  StyleSpecification,
  SymbolLayerSpecification,
  VectorSourceSpecification
} from 'maplibre-gl';
import {SecurityService} from '../../../security/security.service';
import {BaseMapType, ISettingKeyValuePair, SettingsService} from '../../../configuration/settings.service';
import {ConfigurationModel, FeatureFlagEnum, MapLayer} from '../../models/configuration.model';
import {MapLayerType} from '../../models/map-layer.model';
import {Subscription} from 'rxjs';
import {ObservationMapMarkerService} from './services/observation-map-marker.service';
import {VehicleMapMarkerService} from './services/vehicle-map-marker.service';
import {ImageMapMarkerService} from './services/image-map-marker.service';
import {PopupInfoService} from './services/popup-info.service';
import {MapEventService} from './services/map-event.service';
import {TracksVectorTilesLayerService} from './services/tracks-vector-tiles-layer.service';
import {TracksGeoJsonLayerService} from './services/tracks-geo-json-layer.service';
import {AddressLookupMapMarkerService} from './services/address-lookup-map-marker.service';
import {RoadStatusService} from './services/road-status.service';
import {RoadStatusCoverageLayerService} from './services/road-status-coverage-layer.service';
import {RoadStatusCurrencyLayerService} from './services/road-status-currency-layer.service';
import {MapControlService} from './services/map-control.service';
import {RoutesLayerService} from './services/routes-layer.service';
import {WeatherRadarLayerService} from './services/weather-radar-layer.service';
import {WeatherWarningsLayerService} from './services/weather-warnings-layer.service';
import {TrafficLayerService} from './services/traffic-layer.service';
import {ExtAuthService} from '../../../data/ext-auth/ext-auth.service';
import moment from 'moment';
import {environment} from '../../../../environments/environment';
import {RouteSourceService} from './services/route-source.service';
import {StaticRoutesLayerService} from './services/static-routes-layer.service';
import { ToastService } from '../../services/toast.service';
import { TracksDebugLayerService } from './services/tracks-debug-layer.service';

interface ImageDef {
  name: string;
  url: string;
  options: any;
}

export class MapLayersManager {

  // use AccuTerra Maps PROD for PlowOps PROD and
  // AccuTerra Maps STAGING for other PlowOps environments
  public static ACCUTERRA_API_KEY = environment.name === 'local' || environment.name === 'dev'
    ? 'a9EvGjUQ7R88aRqfwve6c70QMKqzXxDC6kOHV5Ww'
    : 'xv77dLihsm7ggIOc8Kn1X64sMFna09u32iBNmiHU';
  public static ACCUTERRA_DOMAIN = environment.name === 'local' || environment.name === 'dev'
    ? 'maps.location-dev.neotreks.com'
    : 'maps.location-prod.neotreks.com';

  public static ACCUTERRA_OUTDOORS_MAP_STYLE =
    `https://${MapLayersManager.ACCUTERRA_DOMAIN}/v1/styles/accuterra-outdoors/style.json?key=${MapLayersManager.ACCUTERRA_API_KEY}`;
  public static ACCUTERRA_OUTDOORS_M_MAP_STYLE =
    `https://${MapLayersManager.ACCUTERRA_DOMAIN}/v1/styles/accuterra-outdoors-m/style.json?key=${MapLayersManager.ACCUTERRA_API_KEY}`;
  public static ACCUTERRA_DARK_MAP_STYLE =
    `https://${MapLayersManager.ACCUTERRA_DOMAIN}/v1/styles/plowops-night/style.json?key=${MapLayersManager.ACCUTERRA_API_KEY}`;
  public static ACCUTERRA_DARK_M_MAP_STYLE =
    `https://${MapLayersManager.ACCUTERRA_DOMAIN}/v1/styles/plowops-night/style.json?key=${MapLayersManager.ACCUTERRA_API_KEY}`;
  public static ACCUTERRA_WINTER_MAP_STYLE =
    `https://${MapLayersManager.ACCUTERRA_DOMAIN}/v1/styles/winter/style.json?key=${MapLayersManager.ACCUTERRA_API_KEY}`;
  public static ACCUTERRA_WINTER_M_MAP_STYLE =
    `https://${MapLayersManager.ACCUTERRA_DOMAIN}/v1/styles/winter-m/style.json?key=${MapLayersManager.ACCUTERRA_API_KEY}`;
  public static IMAGERY_MAP_STYLE =
    `https://${MapLayersManager.ACCUTERRA_DOMAIN}/v1/styles/satellite/style.json?key=${MapLayersManager.ACCUTERRA_API_KEY}`;
  public static IMAGERY_M_MAP_STYLE =
    `https://${MapLayersManager.ACCUTERRA_DOMAIN}/v1/styles/satellite-m/style.json?key=${MapLayersManager.ACCUTERRA_API_KEY}`;

  private layersMap = new Map<string, string>();
  public firstSymbolLayerId: string;
  private readonly imagesMap = new Map<string, ImageDef>();
  private readonly settingsSubscription: Subscription;
  private readonly refreshTimers: Map<string, NodeJS.Timeout> = new Map<string, NodeJS.Timeout>();

  public static async getEmptyStyle(useMetric: boolean): Promise<StyleSpecification> {
    // fetch imagery style
    const response = await fetch(
      useMetric ? MapLayersManager.IMAGERY_M_MAP_STYLE : MapLayersManager.IMAGERY_MAP_STYLE,
    );
    const style = await response.json();

    // rename and mark as custom
    style.name = 'Custom';
    style.metadata = {
      ...style.metadata,
      'custom:id': 'Custom',
      'custom:name': 'Custom',
    };

    // remove all existing layers and sources and add one dummy symbol layer for layer ordering
    style.sources = {};
    style.sources['dummy-source'] = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    };

    // Add dummy layer
    style.layers = [];
    style.layers.push({
      id: 'custom-insertion-point',
      type: 'symbol',
      source: 'dummy-source',
      layout: {
        visibility: 'none'
      }
    });
    return style;
  }

  constructor(
    private map: MapLibre,
    private configuration: ConfigurationModel,
    private settingsService: SettingsService,
    private securityService: SecurityService,
    private observationMapMarkerService: ObservationMapMarkerService,
    private vehicleMapMarkerService: VehicleMapMarkerService,
    private imageMapMarkerService: ImageMapMarkerService,
    private popupInfoService: PopupInfoService,
    private mapEventService: MapEventService,
    private mapControlService: MapControlService,
    private tracksVectorTilesLayerService: TracksVectorTilesLayerService,
    private tracksGeoJsonLayerService: TracksGeoJsonLayerService,
    private tracksDebugLayerService: TracksDebugLayerService,
    private routeSourceService: RouteSourceService,
    private routesLayerService: RoutesLayerService,
    private staticRoutesLayerService: StaticRoutesLayerService,
    private roadStatusService: RoadStatusService,
    private roadStatusCoverageLayerService: RoadStatusCoverageLayerService,
    private roadStatusCurrencyLayerService: RoadStatusCurrencyLayerService,
    private addressLookupMapMarkerService: AddressLookupMapMarkerService,
    private weatherRadarLayerService: WeatherRadarLayerService,
    private weatherWarningsLayerService: WeatherWarningsLayerService,
    private trafficLayerService: TrafficLayerService,
    private extAuthService: ExtAuthService,
    private toastService: ToastService,
  ) {
    this.settingsSubscription =
      this.settingsService.settingsChangedObservable.subscribe(
        (keyValuePair: ISettingKeyValuePair) => {
          this.onSettingsChanged(keyValuePair.key, keyValuePair.value);
        }
      );
  }

  init() {
    this.firstSymbolLayerId = this.getFirstSymbolLayerId();

    // initialize custom basemap
    this.initializeCustomBaseMap();

    // route source and static route layers initialization
    this.routeSourceService.init(this, true);
    this.staticRoutesLayerService.init(this);

    // custom overlays
    this.addOverlayMapLayers();

    // weather layers
    this.weatherRadarLayerService.init(
        this,
        this.hasFeatureFlag(FeatureFlagEnum.Weather),
    );
    this.weatherWarningsLayerService.init(
        this,
        this.hasFeatureFlag(FeatureFlagEnum.Weather),
        this.configuration,
    );
    this.trafficLayerService.init(
        this,
        true,
    );

    // heat layers
    this.roadStatusService.init(
      this,
      this.configuration,
      true,
    );
    this.roadStatusCoverageLayerService.init(
      this,
      true,
    );
    this.roadStatusCurrencyLayerService.init(
      this,
      true,
    );

    // routes layers
    this.routesLayerService.init(this, true);

    // debug track layers
    this.tracksDebugLayerService.init(this, this.configuration, true);

    // tracks
    this.tracksVectorTilesLayerService.init(this, this.configuration, true);
    this.tracksGeoJsonLayerService.init(this, this.configuration, true);

    // image markers
    this.imageMapMarkerService.init(this);

    // observation markers
    this.observationMapMarkerService.init(this);

    // vehicle markers
    this.vehicleMapMarkerService.init(this);


    // vehicle and observation marker info
    this.popupInfoService.init(this.map, this.configuration);

    // address lookup
    this.addressLookupMapMarkerService.init(this);

    // map clicks and events
    this.mapEventService.init(this.map, this.configuration);

    // map manipulation - zooming etc.
    this.mapControlService.init(this);
  }

  release() {
    this.weatherRadarLayerService.release();
    this.weatherWarningsLayerService.release();
    this.trafficLayerService.release();
    this.observationMapMarkerService.release();
    this.imageMapMarkerService.release();
    this.popupInfoService.release();
    this.tracksVectorTilesLayerService.release();
    this.tracksGeoJsonLayerService.release();
    this.tracksDebugLayerService.release();
    this.staticRoutesLayerService.release();
    this.routesLayerService.release();
    this.routeSourceService.release();
    this.roadStatusCoverageLayerService.release();
    this.roadStatusCurrencyLayerService.release();
    this.roadStatusService.release();
    this.vehicleMapMarkerService.release();
    this.addressLookupMapMarkerService.release();
    this.mapEventService.release();
    this.mapControlService.release();
    if (!!this.settingsSubscription) {
      this.settingsSubscription.unsubscribe();
    }
    this.refreshTimers.forEach(refreshTimer => {
      clearTimeout(refreshTimer);
    });
    this.refreshTimers.clear();
    this.firstSymbolLayerId = null;
  }

  addLayers() {
    this.firstSymbolLayerId = this.getFirstSymbolLayerId();

    // initialize custom basemap
    this.initializeCustomBaseMap();

    // custom map layers:

    // first route source as it is used in custom overlays and route assignments
    this.routeSourceService.reloadSource();
    this.staticRoutesLayerService.reset();

    // overlay layers in order defined by indexes
    this.addOverlayMapLayers();
    // weather
    // routes
    // heat layer
    // tracks - vector
    // tracks - geojson
    // tracks - shift
    // images
    // observations
    // vehicles
    // address - tracks
    // address - breadcrumbs
    // address - locations
    this.weatherRadarLayerService.addSourceAndLayer(true);
    this.weatherWarningsLayerService.addSourceAndLayer(true);
    this.trafficLayerService.addSourceAndLayer(true);
    this.roadStatusService.addSource();
    this.roadStatusCoverageLayerService.addLayers();
    this.routesLayerService.reload();
    this.roadStatusCurrencyLayerService.addLayers();
    this.tracksDebugLayerService.addSourcesAndLayers();
    this.tracksVectorTilesLayerService.addSourceAndLayers();
    this.tracksGeoJsonLayerService.addSourceAndLayers();
    this.imageMapMarkerService.addSourceAndLayers();
    this.observationMapMarkerService.createSourceAndLayers();
    this.vehicleMapMarkerService.addSourceAndLayers();
    this.addressLookupMapMarkerService.addSourcesAndLayers();
    this.mapEventService.attachOnClickHandlers();
  }

  /**
   * Adds the layer to the map at a given position.
   * @param layer The layer to add.
   * @param beforeId The layer will be added before another layer with the id.
   */
  addLayer(
    // tslint:disable-next-line:max-line-length
    layer: LineLayerSpecification | FillLayerSpecification | SymbolLayerSpecification | CircleLayerSpecification | RasterLayerSpecification,
    beforeId: string = null
  ) {
    if (this.layersMap.has(layer.id)) {
      console.warn('Trying to add existing layer: ' + layer.id);
      return; // the layer already exists
    }

    this.layersMap.set(layer.id, null);
    if (this.map.getSource(layer.id)) {
      // source already exists on the style
      console.warn('Trying to add existing source: ' + layer.id);
      layer.source = layer.id;
    }
    // console.log(layer);
    this.map.addLayer(layer as LayerSpecification, beforeId);
  }

  moveLayer(layerId: string, beforeId: string) {
    if (this.layersMap.has(layerId) && this.layersMap.has(beforeId)) {
      this.map.moveLayer(layerId, beforeId);
    } else {
      console.warn(
        `Trying to move layer ${layerId} before ${beforeId}. Failed: ${this.layersMap.has(layerId)}, ${this.layersMap.has(beforeId)}`
      );
    }
  }

  hideLayer(layer: LayerSpecification) {
    if (!this.layersMap.has(layer.id)) {
      console.warn('Trying to hide layer: ' + layer.id + ', it doesn\'t exist');
      return; // the layer does not exist
    }
    this.setLayerVisibility(layer.id, false);
  }

  private getFirstSymbolLayerId(): string {
    const layers = this.map.getStyle().layers;
    // Find the index of the first symbol layer in the map style
    let firstSymbolId;
    for (const item of layers) {
      if (item.type === 'symbol') {
        firstSymbolId = item.id;
        break;
      }
    }
    return firstSymbolId;
  }

  updateConfiguration(configuration: ConfigurationModel) {
    this.configuration = configuration;
    if (configuration) {
      this.changeBaseMap(this.getCurrentBaseMap(), true).then();
    }
  }

  getRasterSource(sourceId: string): RasterTileSource {
    return this.map?.getSource(sourceId) as RasterTileSource;
  }

  getGeoJsonSource(sourceId: string): GeoJSONSource {
    return this.map?.getSource(sourceId) as GeoJSONSource;
  }

  getVectorSource(sourceId: string): VectorSourceSpecification {
    return this.map?.getSource(sourceId) as VectorSourceSpecification;
  }

  addSource(sourceId: string, source: SourceSpecification) {
    this.map.addSource(sourceId, source);
  }

  setLayerVisibility(layerId: string, visible: boolean) {
    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none'
      );
    }
  }

  setLayerPaintProperty(layerId: string, property: string, value: any) {
    this.map.setPaintProperty(layerId, property, value);
  }

  setFilter(layerId: string, filter: any) {
    this.map.setFilter(layerId, filter);
  }

  waitUntilStyleLoaded(callback) {
    if (this.map.isStyleLoaded()) {
      callback();
    } else {
      this.map.once('load', callback);
    }
  }

  getCurrentBaseMap() {
    const styleName = this.map.getStyle().name;
    if (styleName.startsWith('Dark')) {
      return BaseMapType.DARK;
    } else if (styleName.startsWith('Winter')) {
      return BaseMapType.WINTER;
    } else if (styleName.startsWith('Labels')) {
      return BaseMapType.IMAGERY;
    } else if (styleName.startsWith('AccuTerra Outdoors')) {
      return BaseMapType.OUTDOORS;
    } else {
      return BaseMapType.CUSTOM;
    }
  }

  async changeBaseMap(mapTypeCode: string, force: boolean = false) {
    const useMetric = this.configuration.useMetricSystem;
    if (mapTypeCode !== this.getCurrentBaseMap() || force) {
      switch (mapTypeCode) {
        case BaseMapType.OUTDOORS:
          this.map.setStyle(
              useMetric ? MapLayersManager.ACCUTERRA_OUTDOORS_M_MAP_STYLE : MapLayersManager.ACCUTERRA_OUTDOORS_MAP_STYLE,
              {diff: !force},
          );
          break;
        case BaseMapType.DARK:
          this.map.setStyle(
              useMetric ? MapLayersManager.ACCUTERRA_DARK_M_MAP_STYLE : MapLayersManager.ACCUTERRA_DARK_MAP_STYLE,
              {diff: !force},
          );
          break;
        case BaseMapType.IMAGERY:
          this.map.setStyle(
            useMetric ? MapLayersManager.IMAGERY_M_MAP_STYLE : MapLayersManager.IMAGERY_MAP_STYLE,
            {diff: !force},
          );
          break;
        case BaseMapType.CUSTOM:
          const style = await MapLayersManager.getEmptyStyle(useMetric);
          this.map.setStyle(style);
          break;
        case BaseMapType.WINTER:
        default:
          this.map.setStyle(
            useMetric ? MapLayersManager.ACCUTERRA_WINTER_M_MAP_STYLE : MapLayersManager.ACCUTERRA_WINTER_MAP_STYLE,
            {diff: !force},
          );
      }

      // once the style is loaded, trigger onChange function
      const that = this;
      this.map.once('styledata', (data) => {
        const waiting = () => {
          if (!that.map.isStyleLoaded()) {
            setTimeout(waiting, 200);
          } else {
            that.onMapStyleChange(force);
          }
        };
        waiting();
      });
    }
  }

  private onMapStyleChange(force: boolean = false) {
    this.layersMap.clear();
    this.refreshTimers.forEach(refreshTimer => {
      clearTimeout(refreshTimer);
    });
    this.refreshTimers.clear();

    if (force) {
      this.addImages();
    }
    this.addLayers();
  }

  refreshMapLayersVisibility(layerInfo: MapLayer) {
    const shouldBeVisible = this.settingsService.getBooleanValue(
        layerInfo.name
    );
    if (layerInfo.type === MapLayerType.RASTER) {
      this.setLayerVisibility(layerInfo.name, shouldBeVisible);
    } else if (layerInfo.type === MapLayerType.VECTOR) {
      let vectorLayers: LayerSpecification[];
      try {
        vectorLayers = JSON.parse(layerInfo.configuration) as LayerSpecification[];
      } catch (e: any) {
        console.error(`Could not parse vector layers for ${layerInfo.name}`);
        this.toastService.short(`Invalid vector layer: ${layerInfo.name}!`);
        return;
      }
      if (vectorLayers && vectorLayers.length > 0) {
        vectorLayers.forEach((layer) => {
          this.setLayerVisibility(layer.id, shouldBeVisible);
        });
      } else {
        console.error(`Invalid configuration for vector layers ${layerInfo.name}`);
        this.toastService.short(`Invalid vector layer: ${layerInfo.name}!`);
      }
    } else if (layerInfo.type === MapLayerType.ROUTE) {
      this.staticRoutesLayerService.setRouteConfigLayersVisibility(layerInfo.routeConfigurationId, shouldBeVisible);
    }
  }

  initializeCustomBaseMap() {
    const targetBaseMap = this.settingsService.getStringValue(SettingsService.BASE_MAP_LAYER_KEY);

    // set custom base map
    if (targetBaseMap === BaseMapType.CUSTOM) {
      // find the symbol layer in the style
      this.firstSymbolLayerId = this.getFirstSymbolLayerId();
      // find the layer in the configuration
      const layerId = this.settingsService.getStringValue(SettingsService.CUSTOM_BASE_MAP_KEY);
      const layer = this.configuration.additionalLayers.find(l => l.useAsBaseMap && l.name === layerId);
      if (layer) {
        // only raster layer with no credentials set can be used as custom basemap
        this.addRasterLayer(layer, true, this.firstSymbolLayerId);
      } else {
        console.warn(`Custom BaseMap: Layer ${layerId} not found`);
      }
    }
  }

  addOverlayMapLayer(layerInfo: MapLayer) {
    if (layerInfo.type === MapLayerType.RASTER) {
      this.addRasterOverlayMapLayer(layerInfo, this.firstSymbolLayerId);
    } else if (layerInfo.type === MapLayerType.VECTOR) {
      this.addVectorOverlayMapLayer(layerInfo);
    } else if (layerInfo.type === MapLayerType.ROUTE) {
      this.addRouteMapLayer(layerInfo);
    }
  }

  private addRasterOverlayMapLayer(layerInfo: MapLayer, beforeId: string = null) {
    const shouldBeVisible = this.settingsService.getBooleanValue(
      layerInfo.name
    );

    if (layerInfo.tokenRequired) {
      this.extAuthService.getToken(layerInfo.url).then(tokenResponse => {
        this.addRasterLayer(layerInfo, shouldBeVisible, beforeId, tokenResponse.data.token);

        console.log(`Token for Map Layer ${layerInfo.name} will expire on ${moment(tokenResponse.data.expires)}`);
        console.log(`Token validity in milliseconds: ${tokenResponse.data.expires - (moment().unix() * 1000)}`);
        const refreshTimer = setTimeout(() => {
          const currentLayerIndex = this.map.style._order.indexOf(layerInfo.name);
          let currentBeforeId: string;
          if (currentLayerIndex > -1 && currentLayerIndex + 1 < this.map.getStyle().layers.length - 1) {
            currentBeforeId = this.map.style._order[currentLayerIndex + 1];
          }
          this.refreshRasterLayer(layerInfo, currentBeforeId);
        }, tokenResponse.data.expires - (moment().unix() * 1000));
        this.refreshTimers.set(layerInfo.name, refreshTimer);
      }).catch(error => {
        console.warn(`Token could not be retrieved! Map Layer ${layerInfo.name} will stay hidden!`);
      });
    } else {
      this.addRasterLayer(layerInfo, shouldBeVisible, beforeId);
    }
  }

  private addRasterLayer(layerInfo: MapLayer, visible: boolean, beforeId: string = null, token: string = null) {
    let tokenParam = '';
    if (!!token) {
      tokenParam = `&token=${token}`;
    }
    const layer = {
      id: layerInfo.name,
      source: `${layerInfo.name}-source`,
      type: 'raster',
      layout: {
        visibility: visible ? 'visible' : 'none',
      },
      paint: {
        'raster-opacity': layerInfo.opacity !== undefined && layerInfo.opacity !== null ? layerInfo.opacity : 1.0,
      },
    } as RasterLayerSpecification;

    const source = {
      type: 'raster',
      tiles: [layerInfo.url + tokenParam],
      tileSize: 256,
    } as RasterSourceSpecification;

    const sourceName = `${layerInfo.name}-source`;
    this.addSource(sourceName, source);
    this.addLayer(layer, beforeId);
  }

  private refreshRasterLayer(layerInfo: MapLayer, beforeId: string) {
    // delete layer, delete source
    this.map.removeLayer(layerInfo.name);
    this.layersMap.delete(layerInfo.name);
    this.map.removeSource(`${layerInfo.name}-source`);

    // add again
    this.addRasterOverlayMapLayer(layerInfo, beforeId);
  }

  private addVectorOverlayMapLayer(layerInfo: MapLayer) {
    const shouldBeVisible = this.settingsService.getBooleanValue(
      layerInfo.name
    );
    let vectorLayers: LayerSpecification[];
    try {
      vectorLayers = JSON.parse(layerInfo.configuration) as LayerSpecification[];
    } catch (e: any) {
      console.error(`Could not parse vector layers for ${layerInfo.name}!`);
      this.toastService.short(`Invalid vector layer: ${layerInfo.name}!`);
      return;
    }
    if (vectorLayers && vectorLayers.length > 0) {
      vectorLayers.forEach((layer) => {
        try {
          this.addLayer(
            layer as LineLayerSpecification | FillLayerSpecification | SymbolLayerSpecification | CircleLayerSpecification,
            this.firstSymbolLayerId,
          );
          if (!shouldBeVisible) {
            this.setLayerVisibility(layer.id, false);
          }
        } catch (e: any) {
          console.error(`Could not add one of the vector layers for ${layerInfo.name}! ID: ${layer.id}`);
          this.toastService.short(`Invalid vector layer: ${layerInfo.name}, ID: ${layer.id}!`);
        }
      });
    }
  }

  private addRouteMapLayer(layerInfo: MapLayer) {
    const shouldBeVisible = this.settingsService.getBooleanValue(
        layerInfo.name
    );
    this.staticRoutesLayerService.addRouteConfigLayers(layerInfo.routeConfigurationId, shouldBeVisible);
  }

  addOverlayMapLayers() {
    this.configuration.additionalLayers
      .filter(l => !l.useAsBaseMap)
      .forEach((additionalLayer) => {
        this.addOverlayMapLayer(additionalLayer);
      });
  }

  zoomTo(location: LngLatLike, zoomLevel: number) {
    this.map.flyTo({
      center: location,
      zoom: zoomLevel,
      essential: true,
    });
  }

  fitTo(locations: LngLatBounds) {
    this.map.fitBounds(locations, {
      padding: {top: 80, bottom: 80, left: 460, right: 540},
      linear: false,
      maxZoom: 15,
      essential: true
    });
  }

  async loadImage(name: string, url: string, options: any) {
    const imageResponse = await this.map.loadImage(url);
    this.map.addImage(name, imageResponse.data, options);
    this.imagesMap.set(name, {name, url, options});
  }

  // reload images on basemap style refresh
  addImages() {
    this.imagesMap.forEach(imageDef => {
      this.loadImage(imageDef.name, imageDef.url, imageDef.options);
    });
  }

  hasFeatureFlag(featureFlag: string): boolean {
    return (
      this.configuration.featureFlags.find(
        (value) => value.isEnabled && value.name === featureFlag
      ) !== undefined
    );
  }

  getMapCenter() {
    return this.map.getCenter();
  }

  onZoom(handler: () => void): () => void {
    const wrapped = () => handler();
    this.map.on('zoom', wrapped);
    return () => this.map.off('zoom', wrapped); // returns unsubscribe fn
  }

  onMove(handler: () => void): () => void {
    const wrapped = () => handler();
    this.map.on('move', wrapped);
    return () => this.map.off('move', wrapped);
  }

  getZoom(): number {
    return this.map.getZoom();
  }

  setTransformRequest(accessToken: string) {
    this.map.setTransformRequest(this.getTransformRequestFunction(accessToken));
  }

  closeAllPopups() {
    this.popupInfoService.closeAllPopups();
  }

  private getTransformRequestFunction(accessToken: string): RequestTransformFunction {
    return (url, resourceType): RequestParameters => {
      if (resourceType === 'Tile' && url.indexOf(environment.services.location) > -1 && !!accessToken) {
        return {
          url,
          headers: { Authorization: 'Bearer ' + accessToken },
        };
      } else {
        return undefined;
      }
    };
  }

  private onSettingsChanged(key: string, value: any) {
    // change base map
    if (key === SettingsService.BASE_MAP_LAYER_KEY) {
      this.changeBaseMap(value).then();
    } else {
      // refresh the layer identified by layer name
      this.configuration.additionalLayers
        .filter(l => !l.useAsBaseMap)
        .forEach(
          (layer: MapLayer, index) => {
            if (layer.name === key) {
              this.refreshMapLayersVisibility(layer);
            }
          }
        );
    }
  }
}
